'use client';

import { useParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import { Color } from '@diritto/common/style/color';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { HttpStatus } from '@diritto/common/utils/requester/httpStatus';
import ErrorStatusView from '@/ui-components/error/ErrorStatusView';
import Section from 'app-components/home/sections/Section';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ArrowIcon } from 'icons/ArrowIcon';
import { UsersIcon } from 'icons/UsersIcon';
import { getDateInfo } from 'libs/attendanceCheck';
import { useGetAttendanceCheckMonthsQuery } from 'queries/activityPoint/useGetAttendanceCheckMonthsQuery';
import { useGetAttendanceCheckParticipateQuery } from 'queries/activityPoint/useGetAttendanceCheckParticipateQuery';
import { useGetAttendanceCheckQuery } from 'queries/activityPoint/useGetAttendanceCheckQuery';
import { useGetSectionsQuery } from 'queries/relay/useGetSectionsQuery';
import { useGetWsEpisodeFirstOpenedInMonthQuery } from 'queries/studio/useGetWsEpisodeFirstOpenedInMonthQuery';
import { Calendar } from 'ui-components/attendanceCheck/Calendar';
import { RewardInfo } from 'ui-components/attendanceCheck/RewardInfo';
import { BlockAuth } from 'ui-components/common/BlockAuth';
import { FullPageLoading } from 'ui-components/common/Loading';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.AttendanceCheck,
};

export const AttendanceCheckPage = () => {
  useInAppHeader({ screen: WebViewScreen.MyAttendanceCheck, title: '출석체크', hideHeader: true });
  useInAppStatusBar(WebViewScreen.MyAttendanceCheck);
  const params = useParams();
  const yearMonthParam = params?.yearMonth ?? '';

  const { tracker } = useTrackingState();
  const router = useRouter();
  const { isLoggedIn, isLoaded: isAuthLoaded, user } = useAuthState();

  const { yearMonth, targetMonth, availPrev, prevYearMonth, availNext, nextYearMonth } = useMemo(
    () => getDateInfo(String(yearMonthParam)),
    [yearMonthParam],
  );

  const handleMoveMonth = useCallback(
    (moveToYearMonth: string) => {
      tracker.track(TrackingEventType.ClickChangeAttendanceCheckMonth, { yearMonth: moveToYearMonth });
      router.replace(`/quest/attendance-check/${moveToYearMonth}`);
    },
    [router, tracker],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewAttendanceCheck, { yearMonth });
  }, [tracker, yearMonth]);

  const { data: attendanceCheckMonthsData } = useGetAttendanceCheckMonthsQuery();
  const attendanceCheckMonth = attendanceCheckMonthsData?.months.find(month => month.yearMonth === yearMonth);

  const { data: attendanceCheckParticipateData } = useGetAttendanceCheckParticipateQuery(attendanceCheckMonth?.id ?? 0);
  const attendanceCheckMonthParticipate = attendanceCheckParticipateData?.attendanceCheckMonthParticipate;

  const { data: attendanceCheckData, error: attendanceCheckError } = useGetAttendanceCheckQuery(yearMonth);
  const { data: attendanceCheckSectionData, error: attendanceCheckSectionError } = useGetSectionsQuery({
    includeAdult: !!user?.isAdult,
    position: SectionPosition.ActivityPoint,
  });
  const { data: episodeFirstOpenedInMonthData } = useGetWsEpisodeFirstOpenedInMonthQuery(yearMonth);

  const rightButtons = useMemo(() => [], []);

  if (!isAuthLoaded || !isLoggedIn) {
    return (
      <MutableHeaderLayout title="출석체크">
        {!isAuthLoaded && <FullPageLoading />}
        {!isLoggedIn && <BlockAuth />}
      </MutableHeaderLayout>
    );
  }

  if (attendanceCheckSectionError || attendanceCheckError) {
    return (
      <MutableHeaderLayout title="">
        <ErrorStatusView statusCode={HttpStatus.NOT_FOUND} />
      </MutableHeaderLayout>
    );
  }

  return (
    <MutableHeaderLayout title="출석체크" showHeaderInApp rightButtons={rightButtons}>
      <ContentBodyContainer>
        <div className="flex flex-col items-center mt-8">
          <div className="flex flex-row items-center justify-center gap-4">
            <button
              type="button"
              aria-label="이전 달로 이동"
              onClick={() => availPrev && handleMoveMonth(prevYearMonth)}
              disabled={!availPrev}
              className={`flex items-center justify-center ${availPrev ? 'opacity-100' : 'opacity-30 cursor-default'}`}>
              <ArrowIcon className="size-[20px]" />
            </button>
            <div className="text-black font-bold text-xl">{targetMonth.month}월 출석체크</div>
            <button
              type="button"
              aria-label="다음 달로 이동"
              onClick={() => availNext && handleMoveMonth(nextYearMonth)}
              disabled={!availNext}
              className={`flex items-center justify-center ${availNext ? 'opacity-100' : 'opacity-30 cursor-default'}`}>
              <ArrowIcon className="size-[20px]" direction="right" />
            </button>
          </div>
          <div className="flex flex-row items-center justify-center gap-2 mt-2 text-gray-600 font-medium text-xxs">
            <div className="flex items-center gap-2">
              <UsersIcon size={19} color={Color.Gray600} />
            </div>
            <div className="flex items-center">총 {attendanceCheckData?.attends.length}회 참여 🔥</div>
          </div>
        </div>
        <Calendar
          yearMonth={yearMonth}
          attendanceChecks={attendanceCheckData?.attends}
          episodeIds={episodeFirstOpenedInMonthData?.episodeIds}
          episodes={episodeFirstOpenedInMonthData?.episodes}
        />
        {attendanceCheckMonth && (
          <RewardInfo
            attendanceCheckMonth={attendanceCheckMonth}
            attendanceCheckMonthParticipate={attendanceCheckMonthParticipate}
            attends={attendanceCheckData?.attends ?? []}
          />
        )}
      </ContentBodyContainer>
      <ul className="flex flex-col gap-8 mt-12">
        {attendanceCheckSectionData?.sections.map(section => (
          <Section
            key={section.id}
            section={section}
            contents={attendanceCheckSectionData.contents}
            episodes={attendanceCheckSectionData.episodes}
            profileLites={attendanceCheckSectionData.profileLites || {}}
            replies={attendanceCheckSectionData.replies}
            fancyShares={attendanceCheckSectionData.fancyShares}
            rollings={attendanceCheckSectionData.rollings || {}}
            trackerExtraInfo={trackerExtraInfo}
          />
        ))}
      </ul>
    </MutableHeaderLayout>
  );
};
